import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { ChevronDown } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useDemoStore } from '../stores/demoStore'
import api from '../services/api'

const IS_DEV = import.meta.env.DEV
const IS_DEMO_BUILD = import.meta.env.VITE_DEMO === 'true'

const BUILT_IN_PERSONAS = [
  { email: 'sarah.mitchell@pacificinfra.com.au', name: 'Sarah Mitchell',  role: 'tenant_admin', roleLabel: 'Admin',          color: '#6554C0' },
  { email: 'j.chen@pacificinfra.com.au',         name: 'James Chen',      role: 'corp_safety',  roleLabel: 'Corp Safety',    color: '#1D4ED8' },
  { email: 'l.nguyen@pacificinfra.com.au',        name: 'Lisa Nguyen',     role: 'ops_safety',   roleLabel: 'Safety Officer', color: '#065F46' },
  { email: 'm.torres@pacificinfra.com.au',        name: 'Michael Torres',  role: 'supervisor',   roleLabel: 'Supervisor',     color: '#92400E' },
  { email: 'j.thompson@pacificinfra.com.au',      name: 'Jack Thompson',   role: 'field_worker', roleLabel: 'Field Worker',   color: '#9F1239' },
  { email: 'r.davis@pacificinfra.com.au',         name: 'Ryan Davis',      role: 'field_worker', roleLabel: 'Field Worker',   color: '#9F1239' },
]

const DEV_ACCOUNTS = [
  { name: 'Brett Waine',    email: 'brett.waine@gmail.com',           role: 'tenant_admin', badge: '#6554C0' },
  { name: 'James Morrison', email: 'j.morrison@acmemetering.com.au',  role: 'corp_safety',  badge: '#0A84FF' },
  { name: 'Sarah Chen',     email: 's.chen@acmemetering.com.au',      role: 'ops_safety',   badge: '#00D4AA' },
  { name: 'Tom Nguyen',     email: 't.nguyen@acmemetering.com.au',    role: 'ops_safety',   badge: '#00D4AA' },
  { name: 'David Smith',    email: 'd.smith@acmemetering.com.au',     role: 'supervisor',   badge: '#FF9500' },
  { name: 'Karen Wilson',   email: 'k.wilson@acmemetering.com.au',    role: 'supervisor',   badge: '#FF9500' },
  { name: 'Mia Patel',      email: 'm.patel@acmemetering.com.au',     role: 'supervisor',   badge: '#FF9500' },
  { name: 'Ryan Jones',     email: 'r.jones@acmemetering.com.au',     role: 'field_worker', badge: '#FF3B5C' },
  { name: 'Amy Lee',        email: 'a.lee@acmemetering.com.au',       role: 'field_worker', badge: '#FF3B5C' },
  { name: 'Ben Taylor',     email: 'b.taylor@acmemetering.com.au',    role: 'field_worker', badge: '#FF3B5C' },
  { name: 'Claire Brown',   email: 'c.brown@acmemetering.com.au',     role: 'field_worker', badge: '#FF3B5C' },
]

const DEV_PASSWORD = 'Acme2026!'

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  tenant_admin:         { bg: '#EDE9FE', color: '#6554C0' },
  corp_safety:          { bg: '#DBEAFE', color: '#1D4ED8' },
  ops_safety:           { bg: '#D1FAE5', color: '#065F46' },
  supervisor:           { bg: '#FEF3C7', color: '#92400E' },
  field_worker:         { bg: '#FFE4E6', color: '#9F1239' },
  contractor_supervisor:{ bg: '#F0FDF4', color: '#166534' },
  contractor_standard:  { bg: '#F0F9FF', color: '#0C4A6E' },
}

function avatarColor(name: string) {
  const colors = ['#6554C0','#0A84FF','#00D4AA','#FF9500','#FF3B5C','#1B2B4B','#2563EB']
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xFFFFFF
  return colors[Math.abs(h) % colors.length]
}

function initials(name: string) {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
}

interface DemoUser {
  id: string
  name: string
  firstName: string
  lastName: string
  role: string
  roleLabel: string
  email: string
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [devOpen, setDevOpen] = useState(false)
  const [demoUsers, setDemoUsers] = useState<DemoUser[]>(() =>
    IS_DEMO_BUILD
      ? BUILT_IN_PERSONAS.map(p => ({ id: p.email, ...p, firstName: p.name.split(' ')[0], lastName: p.name.split(' ')[1] }))
      : []
  )
  const [demoLoading, setDemoLoading] = useState<string | null>(null)
  const { setAuth, logout } = useAuthStore()
  const { reset: resetDemo } = useDemoStore()
  const navigate = useNavigate()

  useEffect(() => {
    logout()
    resetDemo()
    if (!IS_DEMO_BUILD) {
      // Check if demo mode is active on server — silent fail if not
      api.get('/auth/demo-users').then(({ data }) => {
        if (Array.isArray(data) && data.length) setDemoUsers(data)
      }).catch(() => {})
    }
  }, [])

  const afterLogin = (data: any) => {
    setAuth(data.accessToken, data.refreshToken, data.user)
    navigate(data.user.mustChangePassword ? '/change-password' : (Capacitor.isNativePlatform() ? '/m/home' : '/dashboard'))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data } = await api.post('/auth/login', { email: email.trim().toLowerCase(), password: password.trim() })
      afterLogin(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const loginAs = async (accountEmail: string) => {
    setError('')
    setLoading(true)
    setDevOpen(false)
    try {
      const { data } = await api.post('/auth/login', { email: accountEmail, password: DEV_PASSWORD })
      afterLogin(data)
    } catch (err: any) {
      setEmail(accountEmail)
      setPassword(DEV_PASSWORD)
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const demoLogin = async (userId: string) => {
    setError('')
    setDemoLoading(userId)
    try {
      // In demo builds userId is the email; otherwise it's the real UUID
      const body = IS_DEMO_BUILD ? { email: userId } : { userId }
      const { data } = await api.post('/auth/demo-login', body)
      afterLogin(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Demo login failed — check your connection')
    } finally {
      setDemoLoading(null)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div className="w-full" style={{ maxWidth: demoUsers.length ? 480 : 384 }}>
        <div className="text-center mb-8">
          <img src="/logo-mark.svg" alt="HSEQ Intelligence" className="mx-auto mb-4" style={{ width: 72, height: 72 }} />
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ color: 'var(--brand-navy)' }}>hseq<span className="font-medium" style={{ color: 'var(--text-secondary)' }}> intelligence</span></h1>
          <div className="flex items-center justify-center gap-2 mt-2 text-xs font-semibold tracking-widest uppercase">
            <span style={{ color: 'var(--hseq-health)' }}>Health</span>
            <span style={{ color: 'var(--border-strong)' }}>|</span>
            <span style={{ color: 'var(--hseq-safety)' }}>Safety</span>
            <span style={{ color: 'var(--border-strong)' }}>|</span>
            <span style={{ color: 'var(--hseq-environment)' }}>Environment</span>
            <span style={{ color: 'var(--border-strong)' }}>|</span>
            <span style={{ color: 'var(--hseq-quality)' }}>Quality</span>
          </div>
        </div>

        {/* Demo persona picker — shown when DEMO_MODE=true on server */}
        {demoUsers.length > 0 && (
          <div className="card mb-4" style={{ padding: '20px 20px 16px' }}>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 4, background: '#1B2B4B', color: '#fff', letterSpacing: '0.07em' }}>DEMO</span>
              <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Sign in as</span>
              <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>tap to continue</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {demoUsers.map(u => {
                const roleStyle = ROLE_COLORS[u.role] || { bg: '#F1F5F9', color: '#475569' }
                const isLoading = demoLoading === u.id
                return (
                  <button
                    key={u.id}
                    onClick={() => demoLogin(u.id)}
                    disabled={!!demoLoading || loading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 12, textAlign: 'left',
                      background: 'var(--surface)', border: '1.5px solid var(--border)',
                      cursor: demoLoading ? 'wait' : 'pointer',
                      opacity: demoLoading && !isLoading ? 0.5 : 1,
                      transition: 'all 150ms',
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: avatarColor(u.name),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: 'white',
                    }}>
                      {isLoading
                        ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                        : initials(u.name)
                      }
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="font-semibold text-sm truncate" style={{ color: 'var(--text)' }}>{u.name}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: roleStyle.bg, color: roleStyle.color, display: 'inline-block', marginTop: 2 }}>
                        {u.roleLabel}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="text-center text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
              Demo mode — sign in without a password
            </p>
          </div>
        )}

        <div className="card">
          {demoUsers.length > 0 && (
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-muted)' }}>Or sign in with credentials</p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label>Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>

            {error && (
              <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary w-full justify-center" disabled={loading || !!demoLoading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="flex items-center justify-end mt-4 text-sm">
            <Link to="/forgot-password" style={{ color: 'var(--text-muted)' }} className="hover:underline">
              Forgot password?
            </Link>
          </div>
        </div>

        {/* Dev quick-login picker — development only */}
        {IS_DEV && (
        <div
          className="mt-4 rounded-2xl overflow-hidden"
          style={{ border: '1px solid #FF9500', background: 'rgba(255,149,0,0.06)' }}
        >
          <button
            onClick={() => setDevOpen(o => !o)}
            disabled={loading}
            style={{
              width: '100%', padding: '10px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#FF9500', color: '#fff', letterSpacing: '0.05em' }}>DEV</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#FF9500' }}>Test accounts</span>
            </div>
            <ChevronDown size={16} color="#FF9500" style={{ transform: devOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms' }} />
          </button>
          {devOpen && (
            <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {DEV_ACCOUNTS.map(acc => (
                <button
                  key={acc.email}
                  onClick={() => loginAs(acc.email)}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '9px 12px', borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--white)', border: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{acc.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: `${acc.badge}20`, color: acc.badge, letterSpacing: '0.04em' }}>
                    {acc.role.replace('_', ' ')}
                  </span>
                </button>
              ))}
              <p style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginTop: 2 }}>
                All use password: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{DEV_PASSWORD}</span>
              </p>
            </div>
          )}
        </div>
        )}

        <p className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
          Data stored in Australia (AWS ap-southeast-2)
        </p>
      </div>
    </div>
  )
}
